import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Heart,
  ArrowLeft,
  Users,
  HandHeart,
  Clock,
  CheckCircle2,
  AlertCircle,
  PieChart,
  Pill,
  Syringe,
  Wrench,
  Package,
  MoreHorizontal,
  CalendarDays,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { type ConditionWallet, type WalletMembership, iconMap, formatCurrency } from "./shared";

interface DetailedWallet extends ConditionWallet {
  condition?: {
    id: string;
    name: string;
    slug: string;
    description: string;
    iconName: string;
  };
  percentFunded: number;
  topContributors: Array<{
    displayName: string;
    organizationName?: string;
    contributorType: string;
    totalContributed: number | null;
    publicMessage?: string;
  }>;
}

interface UtilizationData {
  totalDisbursedCents: number;
  totalRaisedCents: number;
  membersHelped: number;
  disbursementCount: number;
  categories: Array<{
    purpose: string;
    totalCents: number;
    count: number;
    percentage: number;
  }>;
  recentActivity: Array<{
    purpose: string;
    amountCents: number;
    date: string;
    status: string;
  }>;
  lastUpdated: string | null;
}

const purposeConfig: Record<string, { label: string; icon: typeof Pill; barColor: string; bgColor: string }> = {
  medication: { label: "Medication", icon: Pill, barColor: "bg-blue-500", bgColor: "bg-blue-500/10" },
  treatment: { label: "Treatment", icon: Syringe, barColor: "bg-emerald-500", bgColor: "bg-emerald-500/10" },
  equipment: { label: "Equipment", icon: Wrench, barColor: "bg-amber-500", bgColor: "bg-amber-500/10" },
  supplies: { label: "Supplies", icon: Package, barColor: "bg-purple-500", bgColor: "bg-purple-500/10" },
  other: { label: "Other", icon: MoreHorizontal, barColor: "bg-slate-500", bgColor: "bg-slate-500/10" },
};

export function FundUtilization({ walletId }: { walletId: string }) {
  const { data: utilization, isLoading } = useQuery<UtilizationData>({
    queryKey: ['/api/public/condition-wallets', walletId, 'utilization'],
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </Card>
    );
  }

  if (!utilization || utilization.disbursementCount === 0) {
    return null;
  }

  const utilizationPercent = utilization.totalRaisedCents > 0
    ? Math.round((utilization.totalDisbursedCents / utilization.totalRaisedCents) * 100)
    : 0;

  return (
    <Card className="p-3 sm:p-6" data-testid="card-fund-utilization">
      <div className="flex items-center gap-2 mb-4">
        <PieChart className="w-5 h-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold" data-testid="text-utilization-title">How Funds Are Used</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Transparent, anonymized breakdown of how community contributions support patients.
      </p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="text-xl font-semibold" data-testid="text-total-disbursed">
            {formatCurrency(utilization.totalDisbursedCents)}
          </div>
          <div className="text-xs text-muted-foreground">Funds Used</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-semibold" data-testid="text-members-helped">
            {utilization.membersHelped}
          </div>
          <div className="text-xs text-muted-foreground">Members Helped</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-semibold" data-testid="text-utilization-percent">
            {utilizationPercent}%
          </div>
          <div className="text-xs text-muted-foreground">Funds Deployed</div>
        </div>
      </div>

      <Separator className="mb-6" />

      <div className="space-y-3 mb-6">
        <h3 className="text-sm font-medium text-muted-foreground" data-testid="text-category-heading">Breakdown by Category</h3>
        {utilization.categories.map((cat) => {
          const config = purposeConfig[cat.purpose] || purposeConfig.other;
          const IconComponent = config.icon;
          return (
            <div key={cat.purpose} data-testid={`utilization-category-${cat.purpose}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <IconComponent className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{config.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{formatCurrency(cat.totalCents)}</span>
                  <Badge variant="outline" className="text-xs">{cat.percentage}%</Badge>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${config.barColor}`}
                  style={{ width: `${cat.percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {utilization.recentActivity.length > 0 && (
        <>
          <Separator className="mb-4" />
          <h3 className="text-sm font-medium text-muted-foreground mb-3" data-testid="text-activity-heading">Recent Fund Activity</h3>
          <div className="space-y-2">
            {utilization.recentActivity.slice(0, 5).map((activity, index) => {
              const config = purposeConfig[activity.purpose] || purposeConfig.other;
              const IconComponent = config.icon;
              const activityDate = activity.date ? new Date(activity.date) : null;
              return (
                <div
                  key={index}
                  className="flex items-center justify-between gap-3 py-2 border-b last:border-0"
                  data-testid={`utilization-activity-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bgColor}`}>
                      <IconComponent className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <span className="text-sm font-medium">{config.label} Assistance</span>
                      {activityDate && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarDays className="w-3 h-3" />
                          {activityDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-semibold">{formatCurrency(activity.amountCents)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {utilization.lastUpdated && (
        <p className="text-xs text-muted-foreground mt-4 text-right">
          Last updated: {new Date(utilization.lastUpdated).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </p>
      )}
    </Card>
  );
}

export function WalletDetail({ walletId }: { walletId: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [showMembershipDialog, setShowMembershipDialog] = useState(false);
  const [showContributionDialog, setShowContributionDialog] = useState(false);
  const [consentToShare, setConsentToShare] = useState(false);
  const [consentToContact, setConsentToContact] = useState(false);
  const [consentToPublic, setConsentToPublic] = useState(false);
  const [pledgedMonthlyAmountCents, setPledgedMonthlyAmountCents] = useState(0);
  const [customPledgeAmount, setCustomPledgeAmount] = useState("");
  const [useCustomPledge, setUseCustomPledge] = useState(false);
  
  const [contributionAmount, setContributionAmount] = useState(2500);
  const [contributorName, setContributorName] = useState("");
  const [contributorEmail, setContributorEmail] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [publicMessage, setPublicMessage] = useState("");
  const [contributorType, setContributorType] = useState<"individual" | "corporate" | "foundation">("individual");
  const [organizationName, setOrganizationName] = useState("");
  
  const { data: wallet, isLoading } = useQuery<DetailedWallet>({
    queryKey: ['/api/public/condition-wallets', walletId],
  });
  
  const { data: user } = useQuery<{ id: string; username: string } | null>({
    queryKey: ['/api/user'],
    retry: false,
  });
  
  const { data: existingMembership, isLoading: membershipLoading } = useQuery<WalletMembership>({
    queryKey: ['/api/condition-wallets', walletId, 'membership'],
    enabled: !!user,
    retry: false,
  });
  
  const effectivePledgeCents = useCustomPledge
    ? Math.round((parseFloat(customPledgeAmount) || 0) * 100)
    : pledgedMonthlyAmountCents;

  const applyMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        'POST',
        `/api/condition-wallets/${walletId}/enroll`,
        {
          monthlyAmount: effectivePledgeCents,
          consentToShare,
          consentToContact,
          consentToPublic,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Enrollment Submitted",
        description: effectivePledgeCents > 0
          ? `You've applied to join this fund with a $${(effectivePledgeCents / 100).toFixed(0)}/month pledge. Your application is pending review.`
          : "Your membership application has been submitted for review.",
      });
      setShowMembershipDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/condition-wallets', walletId, 'membership'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/condition-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['/api/public/condition-wallets', walletId] });
    },
    onError: (error: any) => {
      toast({
        title: "Enrollment Failed",
        description: error.message || "Failed to submit application. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  const handleApplyClick = () => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please log in to apply for membership.",
        variant: "destructive",
      });
      setLocation('/auth');
      return;
    }
    setShowMembershipDialog(true);
  };
  
  const handleSubmitApplication = () => {
    applyMutation.mutate();
  };
  
  const contributeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        'POST',
        `/api/condition-wallets/${walletId}/contribute/checkout`,
        {
          amount: contributionAmount,
          contributorName: isAnonymous ? "Anonymous" : contributorName,
          contributorEmail,
          isAnonymous,
          publicMessage,
          contributorType,
          organizationName: contributorType !== "individual" ? organizationName : undefined,
        }
      );
      return response.json();
    },
    onSuccess: (data: { url: string; sessionId: string }) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: any) => {
      toast({
        title: "Contribution Failed",
        description: error.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  const handleContribute = () => {
    if (contributionAmount < 100) {
      toast({
        title: "Invalid Amount",
        description: "Minimum contribution is $1.00",
        variant: "destructive",
      });
      return;
    }
    if (contributorType !== "individual" && !organizationName.trim()) {
      toast({
        title: "Organization Name Required",
        description: `Please enter your ${contributorType === "foundation" ? "foundation" : "organization"} name.`,
        variant: "destructive",
      });
      return;
    }
    contributeMutation.mutate();
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  
  if (!wallet) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground mb-4">Wallet not found.</p>
        <Button variant="outline" onClick={() => setLocation('/conditions')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Conditions
        </Button>
      </Card>
    );
  }
  
  const Icon = wallet.condition?.iconName ? (iconMap[wallet.condition.iconName] || Heart) : Heart;
  
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setLocation('/conditions')}
          data-testid="button-back-conditions"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          All Conditions
        </Button>
        {wallet.condition && (
          <>
            <span className="text-muted-foreground">/</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setLocation(`/conditions/${wallet.conditionId}`)}
            >
              {wallet.condition.name}
            </Button>
          </>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-3 sm:p-6">
            <div className="flex items-start gap-4 mb-6">
              {wallet.condition && (
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold mb-2" data-testid="text-wallet-title">{wallet.title}</h1>
                {wallet.condition && (
                  <Badge variant="outline">{wallet.condition.name}</Badge>
                )}
              </div>
              {wallet.isFeatured && (
                <Badge variant="secondary">Featured</Badge>
              )}
            </div>
            
            <p className="text-muted-foreground mb-6">{wallet.summary}</p>
            
            {wallet.story && (
              <div className="bg-muted/30 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-sm italic">{wallet.story}</p>
              </div>
            )}
          </Card>
          
          {wallet.topContributors && wallet.topContributors.length > 0 && (
            <Card className="p-3 sm:p-6">
              <h2 className="text-lg font-semibold mb-4">Top Supporters</h2>
              <div className="space-y-3">
                {wallet.topContributors.map((contributor, index) => (
                  <div key={index} className="flex items-center justify-between gap-2 py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium">{contributor.displayName}</div>
                      {contributor.organizationName && (
                        <div className="text-sm text-muted-foreground">{contributor.organizationName}</div>
                      )}
                      {contributor.publicMessage && (
                        <div className="text-sm text-muted-foreground italic mt-1">"{contributor.publicMessage}"</div>
                      )}
                    </div>
                    {contributor.totalContributed !== null && (
                      <Badge variant="secondary">{formatCurrency(contributor.totalContributed)}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          <FundUtilization walletId={walletId} />
        </div>
        
        <div className="space-y-6">
          <Card className="p-3 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">Funding Progress</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between gap-2 mb-2">
                  <span className="text-xl sm:text-2xl font-bold text-primary">{formatCurrency(wallet.totalRaised)}</span>
                </div>
                <Progress value={wallet.percentFunded} className="h-3" />
                <div className="flex justify-between gap-2 text-sm text-muted-foreground mt-2">
                  <span>{wallet.percentFunded}% funded</span>
                  <span>Goal: {formatCurrency(wallet.fundingGoal)}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 pt-4 border-t">
                <div className="text-center">
                  <div className="text-xl font-semibold">{wallet.memberCount}</div>
                  <div className="text-xs text-muted-foreground">Members</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-semibold">{wallet.contributorCount}</div>
                  <div className="text-xs text-muted-foreground">Supporters</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-semibold">{wallet.servicesDelivered}</div>
                  <div className="text-xs text-muted-foreground">Funded</div>
                </div>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 sm:p-6 bg-primary/5 border-primary/20">
            <h2 className="text-lg font-semibold mb-3">Support This Fund</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Your contribution helps patients in our community access essential healthcare.
            </p>
            <Button 
              className="w-full" 
              data-testid="button-contribute"
              onClick={() => setShowContributionDialog(true)}
            >
              <HandHeart className="w-4 h-4 mr-2" />
              Contribute Now
            </Button>
          </Card>
          
          <Card className="p-3 sm:p-6">
            <h2 className="text-lg font-semibold mb-3">Need Help?</h2>
            <p className="text-sm text-muted-foreground mb-4">
              If you have this condition and need healthcare assistance, you may be eligible to join this fund.
            </p>
            {existingMembership ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {existingMembership.status === 'pending' && (
                    <>
                      <Clock className="w-4 h-4 text-warning" />
                      <span className="text-warning">Application Pending Review</span>
                    </>
                  )}
                  {existingMembership.status === 'active' && (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-success" />
                      <span className="text-success">You are a Member</span>
                    </>
                  )}
                  {(existingMembership.status === 'suspended' || existingMembership.status === 'revoked') && (
                    <>
                      <AlertCircle className="w-4 h-4 text-destructive" />
                      <span className="text-destructive">Membership {existingMembership.status}</span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <Button 
                variant="outline" 
                className="w-full" 
                data-testid="button-apply-membership"
                onClick={handleApplyClick}
                disabled={membershipLoading}
              >
                <Users className="w-4 h-4 mr-2" />
                Apply for Membership
              </Button>
            )}
          </Card>
        </div>
      </div>
      
      <Dialog open={showMembershipDialog} onOpenChange={setShowMembershipDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply for Membership</DialogTitle>
            <DialogDescription>
              Join the {wallet.title} to receive healthcare funding through community support.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <h4 className="font-medium">Membership Benefits</h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>Access to pre-funded healthcare payments</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>Flexible payment schedules for medical expenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>Community support from contributors who care</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Monthly Contribution (optional)</h4>
              <p className="text-xs text-muted-foreground">
                Add a recurring pledge to help sustain the fund. Select $0 for membership only.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "$0 — Membership only", cents: 0 },
                  { label: "$10 / month", cents: 1000 },
                  { label: "$25 / month", cents: 2500 },
                  { label: "$50 / month", cents: 5000 },
                ].map((opt) => (
                  <Button
                    key={opt.cents}
                    type="button"
                    size="sm"
                    variant={!useCustomPledge && pledgedMonthlyAmountCents === opt.cents ? "default" : "outline"}
                    onClick={() => { setUseCustomPledge(false); setPledgedMonthlyAmountCents(opt.cents); }}
                    data-testid={`button-pledge-${opt.cents}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={useCustomPledge ? "default" : "outline"}
                  onClick={() => setUseCustomPledge(true)}
                >
                  Custom
                </Button>
                {useCustomPledge && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={customPledgeAmount}
                      onChange={(e) => setCustomPledgeAmount(e.target.value)}
                      className="w-20"
                    />
                    <span className="text-muted-foreground text-sm">/ month</span>
                  </div>
                )}
              </div>
              {effectivePledgeCents > 0 && (
                <p className="text-xs text-primary font-medium">
                  Pledging ${(effectivePledgeCents / 100).toFixed(2)}/month
                </p>
              )}
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Privacy & Consent</h4>
              
              <div className="flex items-start space-x-3">
                <Checkbox 
                  id="consent-share" 
                  checked={consentToShare}
                  onCheckedChange={(checked) => setConsentToShare(checked === true)}
                  data-testid="checkbox-consent-share"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="consent-share" className="text-sm font-normal cursor-pointer">
                    Share my membership with fund administrators
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Required for processing fund membership
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Checkbox 
                  id="consent-contact" 
                  checked={consentToContact}
                  onCheckedChange={(checked) => setConsentToContact(checked === true)}
                  data-testid="checkbox-consent-contact"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="consent-contact" className="text-sm font-normal cursor-pointer">
                    Allow fund to contact me about opportunities
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    We may reach out about new programs or benefits
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Checkbox 
                  id="consent-public" 
                  checked={consentToPublic}
                  onCheckedChange={(checked) => setConsentToPublic(checked === true)}
                  data-testid="checkbox-consent-public"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="consent-public" className="text-sm font-normal cursor-pointer">
                    Show me as a member publicly (first name only)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Help inspire others by sharing your participation
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowMembershipDialog(false)}
              data-testid="button-cancel-membership"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitApplication}
              disabled={applyMutation.isPending || !consentToShare}
              data-testid="button-submit-membership"
            >
              {applyMutation.isPending ? "Enrolling…" : "Enroll"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showContributionDialog} onOpenChange={setShowContributionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Support This Fund</DialogTitle>
            <DialogDescription>
              Make a contribution to help {wallet.condition?.name || "patients"} access essential healthcare.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div>
              <Label htmlFor="amount">Contribution Amount</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[1000, 2500, 5000, 10000].map((amt) => (
                  <Button
                    key={amt}
                    variant={contributionAmount === amt ? "default" : "outline"}
                    size="sm"
                    onClick={() => setContributionAmount(amt)}
                    data-testid={`button-amount-${amt}`}
                  >
                    ${amt / 100}
                  </Button>
                ))}
              </div>
              <div className="mt-3">
                <Label htmlFor="custom-amount" className="text-sm text-muted-foreground">Or enter custom amount</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    id="custom-amount"
                    type="number"
                    min="1"
                    value={contributionAmount / 100}
                    onChange={(e) => setContributionAmount(Math.round(parseFloat(e.target.value) * 100) || 0)}
                    className="w-24"
                    data-testid="input-custom-amount"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">I'm contributing as</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {([
                    { value: "individual", label: "Individual" },
                    { value: "corporate", label: "Organization" },
                    { value: "foundation", label: "Foundation" },
                  ] as const).map((type) => (
                    <Button
                      key={type.value}
                      variant={contributorType === type.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setContributorType(type.value)}
                      data-testid={`button-type-${type.value}`}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              {contributorType !== "individual" && (
                <div>
                  <Label htmlFor="org-name">{contributorType === "foundation" ? "Foundation Name" : "Organization Name"}</Label>
                  <Input
                    id="org-name"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder={contributorType === "foundation" ? "Gates Foundation" : "Children's Hospital"}
                    data-testid="input-org-name"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="contributor-name">{contributorType !== "individual" ? "Contact Name" : "Your Name"} (optional)</Label>
                <Input
                  id="contributor-name"
                  value={contributorName}
                  onChange={(e) => setContributorName(e.target.value)}
                  placeholder="John Doe"
                  disabled={isAnonymous}
                  data-testid="input-contributor-name"
                />
              </div>
              
              <div>
                <Label htmlFor="contributor-email">Email (for receipt)</Label>
                <Input
                  id="contributor-email"
                  type="email"
                  value={contributorEmail}
                  onChange={(e) => setContributorEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="input-contributor-email"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="anonymous" 
                  checked={isAnonymous}
                  onCheckedChange={(checked) => setIsAnonymous(checked === true)}
                  data-testid="checkbox-anonymous"
                />
                <Label htmlFor="anonymous" className="text-sm font-normal cursor-pointer">
                  Make my contribution anonymous
                </Label>
              </div>
              
              {!isAnonymous && (
                <div>
                  <Label htmlFor="public-message">Public Message (optional)</Label>
                  <Input
                    id="public-message"
                    value={publicMessage}
                    onChange={(e) => setPublicMessage(e.target.value)}
                    placeholder="Share words of encouragement..."
                    data-testid="input-public-message"
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowContributionDialog(false)}
              data-testid="button-cancel-contribution"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleContribute}
              disabled={contributeMutation.isPending || contributionAmount < 100}
              data-testid="button-submit-contribution"
            >
              {contributeMutation.isPending ? "Processing..." : `Contribute ${formatCurrency(contributionAmount)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
